import { repeatEvery, repeatInterval, repeatTimePeriod } from './RecurrenceInterval.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import createRecurrence from 'owa-recurrence-utils/lib/createRecurrence';
import getIntervalNumericMenuItems from '../utils/getIntervalNumericMenuItems';
import getIntervalUnitMenuItems from '../utils/getIntervalUnitMenuItems';
import getRecurrenceInterval from 'owa-recurrence-utils/lib/getRecurrenceInterval';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import updateRecurrenceIntervalInRecurrencePattern from 'owa-recurrence-utils/lib/updateRecurrenceIntervalInRecurrencePattern';
import { isYearlyRecurrence } from 'owa-recurrence-utils/lib/checkRecurrenceTypes';
import { RecurrenceTypes } from 'owa-recurrence-utils/lib/RecurrenceTypes';
import { DisplayDate } from 'owa-datetime';
import { IDropdown, Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import generateDefaultDropdownStyles from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateDefaultDropdownStyles';
import generateDropdownCalloutStyles from 'owa-component-styling-utils/lib/utils/generateDropdownCalloutStyles';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import getSelectedRecurrenceKey from 'owa-recurrence-utils/lib/getSelectedRecurrenceKey';

const styles = require('./RecurrenceInterval.scss');
const classNames = require('classnames/bind').bind(styles);

export interface RecurrenceIntervalProps extends React.HTMLProps<HTMLDivElement> {
    recurrence: RecurrenceType;
    startDate: DisplayDate;
    colorScheme: ColorScheme;
    onRecurrenceChangedCallback: (recurrence: RecurrenceType) => void;
    className?: string;
}

const RecurrenceInterval: React.FunctionComponent<RecurrenceIntervalProps> = props => {
    const { recurrence, startDate, colorScheme, onRecurrenceChangedCallback, className } = props;

    const interval: number = getRecurrenceInterval(recurrence);
    const intervalNumericOptions: IDropdownOption[] = getIntervalNumericMenuItems();
    const intervalUnitOptions: IDropdownOption[] = getIntervalUnitMenuItems(interval);

    const defaultDropdownStyles = generateDefaultDropdownStyles(colorScheme, {
        root: { padding: '1px' },
        dropdownItems: { maxHeight: '250px' },
    });

    const onNumericChange = React.useCallback(
        (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
            if (!option) {
                return;
            }
            const interval = option.key as number;
            const newRecurrence = updateRecurrenceIntervalInRecurrencePattern(recurrence, interval);
            onRecurrenceChangedCallback(newRecurrence);
        },
        [onRecurrenceChangedCallback, recurrence]
    );

    const onUnitChange = React.useCallback(
        (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
            if (!option) {
                return;
            }
            const recurrenceSelection = option.key as RecurrenceTypes;
            const interval = getRecurrenceInterval(recurrence);
            onRecurrenceChangedCallback(createRecurrence(recurrenceSelection, startDate, interval));
        },
        [onRecurrenceChangedCallback, startDate, recurrence]
    );

    const recurrenceDropdownNumericRef = React.useRef<IDropdown>(null);
    const recurrenceDropdownUnitRef = React.useRef<IDropdown>(null);

    const setNumRef = React.useCallback((ref: IDropdown) => {
        if (ref && !recurrenceDropdownNumericRef.current) {
            recurrenceDropdownNumericRef.current = ref;

            if (!isYearlyRecurrence(recurrence)) {
                recurrenceDropdownNumericRef.current.focus();
            }
        }
    }, []);

    const setUnitRef = React.useCallback((ref: IDropdown) => {
        if (ref && !recurrenceDropdownUnitRef.current) {
            recurrenceDropdownUnitRef.current = ref;

            if (isYearlyRecurrence(recurrence)) {
                recurrenceDropdownUnitRef.current.focus();
            }
        }
    }, []);

    return (
        <div className={classNames(styles.titleLabelAndDropdown, className)}>
            <div id={'recurrenceRepeatIntervalTitle'} className={styles.recurrenceText}>
                {loc(repeatEvery)}
            </div>
            {!isYearlyRecurrence(recurrence) && (
                <Dropdown
                    componentRef={setNumRef}
                    ariaLabel={loc(repeatInterval)}
                    options={intervalNumericOptions}
                    selectedKey={interval}
                    onChange={onNumericChange}
                    calloutProps={{
                        styles: generateDropdownCalloutStyles({
                            // ICalloutContentStyles fields are not optional in fabric.
                            container: {},
                            beak: {},
                            beakCurtain: {},
                            calloutMain: {},
                            root: { minWidth: '51px' },
                        }),
                        gapSpace: 5,
                    }}
                    styles={defaultDropdownStyles}
                    className={styles.dropdown}
                />
            )}
            <Dropdown
                componentRef={setUnitRef}
                ariaLabel={loc(repeatTimePeriod)}
                options={intervalUnitOptions}
                selectedKey={getSelectedRecurrenceKey(recurrence, false /* includeCustom */)}
                onChange={onUnitChange}
                calloutProps={{ styles: generateDropdownCalloutStyles(), gapSpace: 5 }}
                styles={defaultDropdownStyles}
                className={styles.dropdown}
            />
        </div>
    );
};

export default observer(RecurrenceInterval);
